import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Images, Newspaper, Film, Instagram, Settings, Inbox, LogOut, ExternalLink, History, LayoutTemplate, LayoutGrid } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/site/Header";
import { MediaLibrary } from "./MediaLibrary";
import { ContentAdmin, SettingsAdmin, MessagesAdmin } from "./ContentAdmin";
import { StorageBanner, StorageGauge, useStorage } from "./Storage";
import { UploadHistory } from "./UploadHistory";
import { HomepageAdmin } from "./HomepageAdmin";
import { GalleryArranger } from "./GalleryArranger";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem("mroue_token")) { setUser(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem("mroue_token"); setUser(false); });
  }, []);
  return <AuthCtx.Provider value={{ user, setUser }}>{children}</AuthCtx.Provider>;
};

export function AdminLogin() {
  const { user, setUser } = useContext(AuthCtx);
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/admin" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("mroue_token", data.token);
      setUser(data.user); nav("/admin");
    } catch (er) { setErr(errMsg(er)); } finally { setBusy(false); }
  };
  const cls = "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm text-white outline-none focus:border-white";
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b0b0b] px-5" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4">
        <Logo className="mb-8 h-12" />
        <h1 className="font-display text-3xl font-bold text-white">Studio access</h1>
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={cls} data-testid="admin-email-input" />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={cls} data-testid="admin-password-input" />
        {err && <p className="text-sm text-red-400" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} className="w-full bg-white py-3.5 text-sm font-semibold text-black disabled:opacity-60" data-testid="admin-login-button">{busy ? "…" : "Sign in"}</button>
      </form>
    </div>
  );
}

const TABS = [["homepage", "Homepage", LayoutTemplate], ["media", "Photos", Images], ["galleries", "Galleries", LayoutGrid], ["uploads", "Envois", History], ["posts", "Journal", Newspaper], ["videos", "Videos", Film], ["instagram", "Instagram", Instagram], ["settings", "Settings", Settings], ["messages", "Messages", Inbox]];

export function AdminDashboard() {
  const { user, setUser } = useContext(AuthCtx);
  const [tab, setTab] = useState("media");
  const [storage, refreshStorage] = useStorage();
  const [config, setConfig] = useState(null);
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    if (!user) return;
    api.get("/admin/uploads/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/admin/photos").then((r) => setPhotos(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user, tab]);
  if (user === null) return <div className="min-h-screen bg-[#0b0b0b]" />;
  if (!user) return <Navigate to="/admin/login" replace />;
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); localStorage.removeItem("mroue_token"); setUser(false); };
  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0b0b0b]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 md:px-8">
          <div className="flex items-center gap-4"><Logo className="h-7" /><span className="kicker hidden sm:inline">Studio</span></div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-white" data-testid="admin-view-site"><ExternalLink size={14} /> Site</Link>
            <span className="hidden font-mono text-xs text-white/50 md:inline">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 text-white/70 hover:text-white"><LogOut size={14} /> Logout</button>
          </div>
        </div>
        <nav className="no-scrollbar mx-auto flex max-w-[1600px] gap-1 overflow-x-auto px-5 md:px-8">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm transition-colors ${tab === k ? "border-[#5d9a35] text-white" : "border-transparent text-white/60 hover:text-white"}`}><Icon size={15} /> {label}</button>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-[1600px] px-5 py-8 md:px-8">
        <StorageBanner s={storage} />
        {tab === "media" && (
          <div className="grid gap-8 xl:grid-cols-12">
            <div className="xl:col-span-9"><MediaLibrary config={config} refreshStorage={refreshStorage} /></div>
            <div className="xl:col-span-3"><div className="xl:sticky xl:top-32"><StorageGauge s={storage} /></div></div>
          </div>
        )}
        {["posts", "videos", "instagram"].includes(tab) && <ContentAdmin name={tab} photos={photos} />}
        {tab === "galleries" && <GalleryArranger />}
        {tab === "homepage" && <HomepageAdmin photos={photos} />}
        {tab === "uploads" && <UploadHistory onChange={refreshStorage} />}
        {tab === "settings" && <SettingsAdmin photos={photos} />}
        {tab === "messages" && <MessagesAdmin />}
      </main>
    </div>
  );
}

export const adminErr = errMsg;
